import { t, TFunction } from "i18next";
import { z } from "zod";
import { ALLOWED_FILE_TYPES, MAX_FILE_SIZE, MAX_FILENAME_LENGTH, SUPPORTED_FILE_TYPES_STRING } from "../constants/documents";

// Helper to convert strings to numbers
export const stringToNumber = (val: unknown) => (typeof val === "string" ? parseFloat(val) : val);

// Helper to convert strings to numbers or undefined
export const stringToNumberOrUndefined = (val: unknown) => {
  if (typeof val === "string") {
    const parsed = parseFloat(val);
    return isNaN(parsed) ? undefined : parsed;
  }
  if (typeof val === "number" && isNaN(val)) {
    return undefined;
  }
  return val;
};

/** Form helpers */

export const nonEmptyString = (fieldName: string) => z.string({
  error: (issue) => issue.input === undefined ?
    `${fieldName} ${t("schemas:form.isrequired")}`
    :
    `${fieldName} ${t("schemas:form.bestring")}`,
}).min(2, t("schemas:form.atleast2chars"));

export const optionalString = (fieldName: string) => z.string({
  error: (issue) => issue.input === undefined ?
    "" // We do not need to add a required error message for optional strings
    :
    `${fieldName} ${t("schemas:form.bestring")}`,
}).optional();

// Schema for positive integers
export const positiveInteger = (fieldName: string) =>
  z.preprocess(
    stringToNumber,
    z.number({
      error: (issue) => issue.input === undefined ?
        `${fieldName} ${t("schemas:form.isrequired")}`
        :
        `${fieldName} ${t("schemas:form.beinteger")}`,
    }).int().positive(`${fieldName} ${t("schemas:form.bepositiveinteger")}`)
  );

// Schema for non-negative integers (including zero)
export const nonNegativeInteger = (fieldName: string) =>
  z.preprocess(
    stringToNumber,
    z.number({
      error: (issue) => issue.input === undefined ?
        `${fieldName} ${t("schemas:form.isrequired")}`
        :
        `${fieldName} ${t("schemas:form.isrequired")}`,
    }).int().min(0, `${fieldName} ${t("schemas:form.benonnegativeinteger")}`)
  );

// Schema for positive numbers
export const positiveNumber = (fieldName: string) =>
  z.preprocess(
    stringToNumber,
    z.number({
      error: (issue) => issue.input === undefined ?
        `${fieldName} ${t("schemas:form.isrequired")}`
        :
        `${fieldName} ${t("schemas:form.notbeempty")}`,
    }).positive(`${fieldName} ${t("schemas:form.bepositivenumber")}`)
  );

// Schema for non-negative numbers (including zero)
export const nonNegativeNumber = (fieldName: string) =>
  z.preprocess(
    stringToNumber,
    z.number({
      error: (issue) => issue.input === undefined ?
        `${fieldName} ${t("schemas:form.isrequired")}`
        :
        `${fieldName} ${t("schemas:form.benumber")}`,
    }).min(0, `${fieldName} ${t("schemas:form.benonnegativenumber")}`)
  );

// New helper function for employee fluctuation (0-100%)
export const postiveNumberZeroToOneHundred = (fieldName: string) =>
  z.preprocess(
    stringToNumberOrUndefined,
    z.number({
      error: (issue) => issue.input === undefined ?
        `${fieldName} ${t("schemas:form.isrequired")}`
        :
        `${fieldName} ${t("schemas:form.benumber")}`,
    })
      .min(0, `${fieldName} ${t("schemas:form.atleast0")}`)
      .max(100, `${fieldName} ${t("schemas:form.atmax100")}`)
  );

// Zod Schema for a date
export const createDateSchema = (t: TFunction) => {
  return z.date({
    error: (issue) => issue.input === undefined ?
      t("schemas:form.daterequired")
      :
      t("schemas:form.dateinvalid"),
  }).refine((date) => date instanceof Date && !isNaN(date.getTime()), {
    message: t("schemas:form.dateinvalid"),
  });
};

// Zod Schema for files array
export const requiredFiles = () => {
  return z
    .array(z.any(), {
      error: (issue) => issue.input === undefined ?
        t("schemas:createtender.document.filerequired")
        :
        "" // The invalid_type error message is handled by the refine function below
    })
    .nonempty({ message: t("schemas:createtender.document.filerequired") })
    .refine(
      (files) => files.every((file) => file instanceof File),
      { message: t("schemas:createtender.document.invalidfiletype", { filetypes: SUPPORTED_FILE_TYPES_STRING }) }
    )
    .refine(
      (files) => files.every((file) => ALLOWED_FILE_TYPES.includes(file.type)),
      { message: t("schemas:createtender.document.invalidfiletype", { filetypes: SUPPORTED_FILE_TYPES_STRING }) }
    )
    .refine(
      (files) => files.every((file) => file.size <= MAX_FILE_SIZE),
      { message: t("schemas:createtender.document.filesizeinvalid") }
    )
    .refine(
      (files) => files.every((file) => file.name.length <= MAX_FILENAME_LENGTH),
      { message: t("schemas:createtender.document.filenametoolong") }
    );
};

// Zod Schema for optional files
export const optionalFiles = () => {
  return z
    .array(z.any())
    .optional()
    .refine(
      (files) => files == undefined || files.every((file) => file instanceof File),
      { message: t("schemas:createtender.document.invalidfiletype", { filetypes: SUPPORTED_FILE_TYPES_STRING }) }
    )
    .refine(
      (files) => files == undefined || files.every((file) => ALLOWED_FILE_TYPES.includes(file.type)),
      { message: t("schemas:createtender.document.invalidfiletype", { filetypes: SUPPORTED_FILE_TYPES_STRING }) }
    )
    .refine(
      (files) => files == undefined || files.every((file) => file.size <= MAX_FILE_SIZE),
      { message: t("schemas:createtender.document.filesizeinvalid") }
    )
    .refine(
      (files) => files == undefined || files.every((file) => file.name.length <= MAX_FILENAME_LENGTH),
      { message: t("schemas:createtender.document.filenametoolong") }
    );
};

// Zod Schema for optional files that only accepts XLSX files
export const optionalFilesXlsxOnly = () => {
  const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  return z
    .array(z.any())
    .optional()
    .refine(
      (files) => files == undefined || files.every((file) => file instanceof File),
      { message: t("schemas:createtender.document.invalidfiletype", { filetypes: "XLSX" }) }
    )
    .refine(
      (files) => files == undefined || files.every((file) => file.type === XLSX_MIME_TYPE),
      { message: t("schemas:createtender.document.invalidfiletype", { filetypes: "XLSX" }) }
    )
    .refine(
      (files) => files == undefined || files.every((file) => file.size <= MAX_FILE_SIZE),
      { message: t("schemas:createtender.document.filesizeinvalid") }
    )
    .refine(
      (files) => files == undefined || files.every((file) => file.name.length <= MAX_FILENAME_LENGTH),
      { message: t("schemas:createtender.document.filenametoolong") }
    );
};

// Zod Schema for a single file
// Uses array limited to 1 to make it compatible with the FileUploadComponent
export const requiredFile = () => {
  return z.array(
    z.instanceof(File)
      .refine(
        (file) => ALLOWED_FILE_TYPES.includes(file.type),
        { message: t("schemas:createtender.document.invalidfiletype", { filetypes: SUPPORTED_FILE_TYPES_STRING }) }
      )
      .refine(
        (file) => file.size <= MAX_FILE_SIZE,
        { message: t("schemas:createtender.document.filesizeinvalid") }
      )
      .refine(
        (file) => file !== null && file !== undefined,
        { message: t("schemas:createtender.document.filerequired") }
      )
      .refine(
        (file) => file.name.length <= MAX_FILENAME_LENGTH,
        { message: t("schemas:createtender.document.filenametoolong") }
      ),
    {
      error: (issue) => issue.input === undefined ?
        t("schemas:form.required")
        :
        t("schemas:createtender.document.invalidfiletype", { filetypes: SUPPORTED_FILE_TYPES_STRING })
    }
  ).length(1, { message: t("schemas:form.required") });
};

/**
 * Function used to clear a form after submission
 * @param {string} elementID ID of the form element
 */
export function clearForm(elementID: string) {
  const formElement = document.getElementById(elementID) as HTMLFormElement;

  formElement.reset();
}

/**
 * International email validation that supports German umlauts (ä, ö, ü, ß) and other international characters.
 * This function creates a Zod schema for email validation that accepts Unicode characters.
 *
 * @param errorMessage - The error message to display for invalid emails
 * @returns Zod schema for international email validation
 *
 * @example
 * // Usage in form schema
 * const schema = z.object({
 *   email: internationalEmail("Invalid email address")
 * });
 */
export const internationalEmail = (errorMessage: string) => {
  // Enhanced regex that supports international characters including German umlauts
  // This regex allows Unicode letters, numbers, and common email special characters
  const internationalEmailRegex = /^[a-zA-Z0-9._%+äöüÄÖÜßàáâãåæçèéêëìíîïñòóôõøùúûüýÿ-]+@[a-zA-Z0-9.äöüÄÖÜßàáâãåæçèéêëìíîïñòóôõøùúûüýÿ-]+\.[a-zA-Z]{2,}$/u;

  return z.string()
    .min(1, errorMessage)
    .refine(
      (email) => internationalEmailRegex.test(email),
      { message: errorMessage }
    );
};

export const onInvalid = (errors: unknown) => console.error(errors);
